import EventEmitter from 'methods/eventEmitter';
import {formatMoney, observeElement, loadScript, isMobile, checkFavoriteList} from 'methods/utils';
import BalloonNearbyHotel from './mob/balloon/nearby-hotel';
import {
    applyYandexMapThemeFromSite,
    bindYandexMapThemeSync,
} from 'components/yandex-map-theme';

const closestHotelName = 'closest-hotel';

function MapApi(options = {}) {
    this._$mapContainer      = document.getElementById('MapContainer');
    this._$panoramaContainer = document.getElementById('PanoramaContainer');
    this._map                = null;
    this._data               = options.data;
    this._hotelMap           = options.hotelMap;
    this._options            = Object.assign({
        useExternalBalloons: {},
    }, options);

    let $container = document.getElementById('Map');
    if (!$container) {
        return;
    }

    if (this._options?.useExternalBalloons?.nearbyHotel) {
        this.balloonNearbyHotel = new BalloonNearbyHotel();
    }

    this._whenInitialized = new Promise((resolve) => {
        this._whenInitializedResolve = resolve;
    });

    this.whenInitialized = function(cb) {
        return this._whenInitialized.then(cb);
    }

    // грузим api карт
    this._loadYandexMapScript = (callback) => {
        loadScript('//api-maps.yandex.ru/2.1/?lang=ru_RU&apikey=34b994e4-e2ef-4a40-a173-61bf0fceb79f').then(() => {
            global.ymaps.ready(() => {
                this._ymaps = global.ymaps;

                if (typeof callback === 'function') {
                    callback();
                }
            });
        });
    }

    // создаем карту
    this._createMap = () => {
        if (!this._ymaps) {
            return this._loadYandexMapScript(this.createMap);
        }

        this._data.behaviors = typeof this._data.behaviors === 'undefined' ? [] : this._data.behaviors;
        this._data.controls = typeof this._data.controls === 'undefined' ? [] : this._data.controls;

        if (!this._map) {
            this._map = new this._ymaps.Map('MapContainer', {
                center: [this._data.latitude, this._data.longitude],
                zoom: this._data.zoom,
                behaviors: this._data.behaviors,
                controls: this._data.controls,
            }, {
                suppressMapOpenBlock: true,
                yandexMapDisablePoiInteractivity: true,
                balloonPanelMaxMapArea: 0
            });

            applyYandexMapThemeFromSite(this._map, this._ymaps);
            bindYandexMapThemeSync(this._map, this._ymaps);

            // закрытие балуна при клике на карту
            this._map.events.add('click', e => {
                this._map.balloon.close();

                if (this.balloonNearbyHotel) {
                    this.balloonNearbyHotel.close();
                }
            });

            // кастомные фильтры
            const filters = {
                // фильтр для цены, используется в шаблоне {{ properties.min_price|money }}
                money: function(data, value, filterValue) {
                    return formatMoney(value, 2, '.', ' ');
                },
            }

            // добавляем кастомные фильтры
            Object.keys(filters).forEach(filterName => this._ymaps.template.filtersStorage.add(filterName, filters[filterName]));

            this.useCustomControls();

            this._whenInitializedResolve();
        }
    }

    // удаляем карту
    this.fitToViewport = () => {
        if (this._map && this._map.container) {
            this._map.container.fitToViewport();
        }
    }

    // добавляем маркер
    this._addMarker = (data, properties) => {
        if (!this._ymaps) {
            return this._loadYandexMapScript(() => {
                this.addMarker(data);
            });
        }

        let icon = {};

        // иконка дома
        if (data.icon === 'bigBlueHomeNew') {
            icon = this._getClosestHotelIcon({
                iconLayout:         'default#image',
                iconImageHref:      '/app/src/img/map/bigBlueHomeNew.svg',
                iconImageSize:      [56, 56],
                iconImageOffset:    [-28, -28],
                hideIconOnBalloonOpen: false,
            });
        } else if (data.icon == closestHotelName) {
            icon = this._getClosestHotelIcon();
        } else {
            const {balloonLayout, balloonContentLayout} = this._getPlaceBalloonLayout(properties.is_current);


            icon = {
                iconLayout:      'default#image',
                iconImageHref:   `/app/src/img/svg/places${properties.is_current ? '/current' : ''}/${data.icon}.svg`,
                iconImageSize:   properties.is_current ? (isMobile() ? [32, 32] : [56, 56]) : [25, 25],
                iconImageOffset: properties.is_current ? (isMobile() ? [-16, -16] : [-28, -28]) : [-12, -12],
                hideIconOnBalloonOpen: false,
                balloonLayout,
                balloonContentLayout,
            };
        }

        let placemark = new this._ymaps.Placemark([+data.latitude, +data.longitude], properties, icon);

        // события Placemark
        data.events = typeof data.events === 'undefined' ? {} : data.events;
        for (let i in data.events) {
            placemark.events.add(i, data.events[i]);
        }

        if (typeof icon.events != 'undefined') {
            for (const eventName in icon.events) {
                placemark.events.add(eventName, icon.events[eventName].bind(this, placemark));
            }
        }

        this._map.geoObjects.add(placemark);
    }

    this._getClosestHotelIcon = function(payload) {
        const obj = this;

        const hotelIconLayout = this._ymaps.templateLayoutFactory.createClass(
            `<div class="Map__placemark js-mapPlacemark" data-id="{{ properties.id }}">
                {{ properties.min_price|money }} руб.
            </div>`,
            {
                // Определяем метод getShape, который
                // будет возвращать размеры макета хинта.
                // Это необходимо для того, чтобы хинт автоматически
                // сдвигал позицию при выходе за пределы карты.
                getShape: function() {
                    const $el = this.getElement();
                    if (!$el || !$el.firstElementChild) {
                        return;
                    }

                    const $placemark = $el.firstElementChild;

                    const offsetMultiplierX = -0.5;
                    const offsetMultiplierY = -0.5;

                    const offsetX = 0;
                    const offsetY = 0;

                    const x = $placemark.offsetWidth * offsetMultiplierX + offsetX;
                    const y = $placemark.offsetHeight * offsetMultiplierY + offsetY;

                    $placemark.style.transform = `translate(${x}px, ${y}px)`;

                    const result = new obj._ymaps.shape.Rectangle(
                        new ymaps.geometry.pixel.Rectangle([
                            [x, y],
                            [x + $placemark.offsetWidth, y + $placemark.offsetHeight]
                        ])
                    );

                    return result;
                },
            }
        );

        const MyBalloonLayout = obj._ymaps.templateLayoutFactory.createClass(
            `<div class="Map__balloon Map__balloon--hotel js-mapBalloon">
                 <div class="Map__balloon-wrapper">
                     $[[options.contentLayout]]
                 </div>
            </div>`, {
                /**
                 * Строит экземпляр макета на основе шаблона и добавляет его в родительский HTML-элемент.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#build
                 * @function
                 * @name build
                 */
                build: function () {
                    this.constructor.superclass.build.call(this);

                    this.$element = this.getElement().firstElementChild;

                    this.$element
                        .querySelector('.js-objectBalloonClose')
                        .addEventListener('click', (evt) => {
                            this.onCloseClick(evt);
                        });

                    // отмечаем избранное
                    checkFavoriteList();
                },

                /**
                 * Удаляет содержимое макета из DOM.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#clear
                 * @function
                 * @name clear
                 */
                clear: function () {
                    this.constructor.superclass.clear.call(this);
                },

                /**
                 * Метод будет вызван системой шаблонов АПИ при изменении размеров вложенного макета.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 * @function
                 * @name onSublayoutSizeChange
                 */
                onSublayoutSizeChange: function () {
                    MyBalloonLayout.superclass.onSublayoutSizeChange.apply(this, arguments);

                    if(!this.$element) {
                        return;
                    }

                    this.events.fire('shapechange');
                },

                /**
                 * Закрывает балун при клике на крестик, кидая событие "userclose" на макете.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 * @function
                 * @name onCloseClick
                 */
                onCloseClick: function (e) {
                    e.preventDefault();

                    this.events.fire('userclose');
                },

                /**
                 * Используется для автопозиционирования (balloonAutoPan).
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/ILayout.xml#getClientBounds
                 * @function
                 * @name getClientBounds
                 * @returns {Number[][]} Координаты левого верхнего и правого нижнего углов шаблона относительно точки привязки.
                 */
                getShape: function () {
                    const offsetMultiplierX = -0.5;
                    const offsetMultiplierY = -1.0;

                    const offsetX = 0;
                    const offsetY = -40;

                    const x = this.$element.offsetWidth * offsetMultiplierX + offsetX;
                    const y = isMobile()
                        ? offsetY
                        : this.$element.offsetHeight * offsetMultiplierY + offsetY;

                    this.$element.style.transform = `translate(${x}px, ${y}px)`;

                    const result = new obj._ymaps.shape.Rectangle(
                        new ymaps.geometry.pixel.Rectangle([
                            [x, y],
                            [
                                x + this.$element.offsetWidth,
                                isMobile()
                                    ? y - this.$element.offsetHeight
                                    : y + this.$element.offsetHeight
                            ]
                        ])
                    );

                    return result;
                },
            });

        const options = payload ? payload : {
            name: closestHotelName,
            zIndex: 10,
            iconLayout: hotelIconLayout,
            iconOffset: [0, 0],
            openBalloonOnClick: !this.balloonNearbyHotel,
            hideIconOnBalloonOpen: false,
            events: {
                click: (placemark, e) => {
                    this._map.balloon.close();

                    if (this.balloonNearbyHotel) {
                        this.balloonNearbyHotel.setData(placemark.properties._data);
                        this.balloonNearbyHotel.open();
                    }
                },
                mouseenter: (placemark, e) => {
                    placemark.getOverlay().then($overlay => {
                        const $icon = $overlay.getElement();
                        const $placemark = $icon.querySelector('.js-mapPlacemark');
                        $placemark.classList.toggle('is-active', true);
                    });
                },
                mouseleave: (placemark, e) => {
                    placemark.getOverlay().then($overlay => {
                        const $icon = $overlay.getElement();
                        const $placemark = $icon.querySelector('.js-mapPlacemark');
                        $placemark.classList.toggle('is-active', false);
                    });
                },
            },
        }

        return {
            ...options,
            balloonLayout: MyBalloonLayout,
            // Создание вложенного макета содержимого балуна.
            balloonContentLayout: obj._ymaps.templateLayoutFactory.createClass(
                `<div class="Balloon-hotel">
                    <div class="Balloon-hotel__wrapper">
                        <button class="Balloon-hotel__close js-objectBalloonClose" title="Закрыть карточку" aria-role="button">
                            <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" style="display: block; fill: none; height: 12px; width: 12px; stroke: #FFFFFF; stroke-width: 4; overflow: visible;">
                                <path d="m6 6 20 20"></path><path d="m26 6-20 20"></path>
                            </svg>
                        </button>
                        <div class="Balloon-hotel__favorite js-objectFavorite" data-id="{{ properties.id }}" title="Добавить в избранное" aria-role="button">
                            <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path class="heart-fill" opacity="0.5" fill-rule="evenodd" clip-rule="evenodd" d="M0 6.88595C0 3.09693 3.10038 0 6.89362 0C8.91634 0 10.747 0.851274 12 2.25509C13.253 0.851274 15.0837 0 17.1064 0C20.8996 0 24 3.09693 24 6.88595C24 8.64637 23.1963 10.3351 22.0964 11.8334C20.9906 13.3396 19.5329 14.7249 18.1021 15.8943C16.6674 17.0668 15.2366 18.0409 14.1665 18.7211C13.6308 19.0617 13.1836 19.3297 12.8692 19.5133C12.712 19.605 12.5878 19.6758 12.5023 19.7239C12.4595 19.748 12.4263 19.7665 12.4035 19.7791L12.3772 19.7936L12.3699 19.7976L12.3678 19.7987C12.3678 19.7987 12.3668 19.7993 12 19.1276C11.6309 19.798 11.6305 19.7978 11.6305 19.7978L11.6277 19.7963L11.6205 19.7923L11.5942 19.7777C11.5714 19.765 11.5383 19.7464 11.4955 19.7221C11.41 19.6736 11.2859 19.6024 11.1287 19.51C10.8144 19.3251 10.3674 19.0554 9.83172 18.7128C8.7619 18.0288 7.33143 17.05 5.89714 15.8745C4.46665 14.7021 3.00941 13.3156 1.90397 11.8124C0.804793 10.3177 0 8.63487 0 6.88595ZM12 19.1276L11.6305 19.7978L11.9985 20L12.3668 19.7993L12 19.1276Z" fill="#2A2536"/>
                                <path class="heart-stroke" fill-rule="evenodd" clip-rule="evenodd" d="M0 6.88595C0 3.09693 3.10038 0 6.89362 0C8.91634 0 10.747 0.851274 12 2.25509C13.253 0.851274 15.0837 0 17.1064 0C20.8996 0 24 3.09693 24 6.88595C24 8.64637 23.1963 10.3351 22.0964 11.8334C20.9906 13.3396 19.5329 14.7249 18.1021 15.8943C16.6674 17.0668 15.2366 18.0409 14.1665 18.7211C13.6308 19.0617 13.1836 19.3297 12.8692 19.5133C12.712 19.605 12.5878 19.6758 12.5023 19.7239C12.4595 19.748 12.4263 19.7665 12.4035 19.7791L12.3772 19.7936L12.3699 19.7976L12.3678 19.7987C12.3676 19.7989 12.3668 19.7993 12 19.1276C11.6309 19.798 11.6307 19.7979 11.6305 19.7978L11.6277 19.7963L11.6205 19.7923L11.5942 19.7777C11.5714 19.765 11.5383 19.7464 11.4955 19.7221C11.41 19.6736 11.2859 19.6024 11.1287 19.51C10.8144 19.3251 10.3674 19.0554 9.83172 18.7128C8.7619 18.0288 7.33143 17.05 5.89714 15.8745C4.46665 14.7021 3.00941 13.3156 1.90397 11.8124C0.804793 10.3177 0 8.63487 0 6.88595ZM12 19.1276L11.6305 19.7978L11.9985 20L12.3668 19.7993L12 19.1276ZM12.0014 18.2472C12.0314 18.2299 12.063 18.2115 12.0962 18.1921C12.3962 18.017 12.8267 17.759 13.3441 17.4301C14.3804 16.7714 15.7581 15.8328 17.132 14.71C18.5096 13.5841 19.8604 12.2914 20.8611 10.9285C21.8675 9.55761 22.4681 8.18596 22.4681 6.88595C22.4681 3.94204 20.0536 1.53021 17.1064 1.53021C15.2222 1.53021 13.5772 2.46885 12.6462 3.93025L12 4.9446L11.3538 3.93025C10.4228 2.46885 8.77776 1.53021 6.89362 1.53021C3.94643 1.53021 1.53191 3.94204 1.53191 6.88595C1.53191 8.17196 2.13138 9.53683 3.13859 10.9065C4.13952 12.2676 5.4908 13.5621 6.86882 14.6915C8.24304 15.8177 9.62108 16.7612 10.6576 17.4241C11.1752 17.755 11.6058 18.0148 11.9058 18.1912C11.9394 18.211 11.9713 18.2296 12.0014 18.2472Z" fill="white"/>
                            </svg>
                        </div>
                        <div class="Balloon-hotel__image">
                            <img src="{{ properties.image_url }}" alt="{{ properties.image_alt }}">
                        </div>
                        <div class="Balloon-hotel__body">
                            <a href="{{ properties.url }}" title="{{ properties.a_title }}" target="_blank" class="Balloon-hotel__link Balloon-hotel__link--absolute"></a>
                            {% if properties.reviews_rating %}
                                <div class="average-rating" data-rating="{{ properties.reviews_rating }}">{{ properties.reviews_rating_formatted }}</div>
                            {% endif %}
                            {% if properties.reviews_count %}
                                <div class="Balloon-hotel__review">{{ properties.reviews_count }} {{ properties.reviews_count_title }}</div>
                            {% endif %}
                            <div class="Balloon-hotel__title" title="{{ properties.title }}">{{ properties.title }}</div>
                            <div class="Balloon-hotel__description" title="{{ properties.city_title }}, {{ properties.distance_title }}">
                                <div class="Balloon-hotel__description-city">{{ properties.city_title }}</div>
                                {% if properties.distance_title %}
                                    <div class="Balloon-hotel__description-distance">{{ properties.distance_title }}</div>
                                {% endif %}
                            </div>
                            <div class="Balloon-hotel__price">
                                <div class="Balloon-hotel__price-cost">
                                    <span class="main-price main-price_mini">от {{ properties.min_price|money }} ₽</span>
                                </div>
                                {% if properties.price_title %}
                                    <div class="Balloon-hotel__price-duration">{{ properties.price_title }}</div>
                                {% endif %}
                            </div>
                            {% if properties.badges %}
                                <div class="Balloon-hotel__badges">
                                    {% for badge in properties.badges %}
                                        <div class="Balloon-hotel__badge Balloon-hotel__badge--{{ badge.type }}" data-hint-content="{{ badge.tooltip }}">
                                            {% if badge.type == 'icon' %}
                                                <div class="Balloon-hotel__badge-icon">
                                                    <img src="/app/src/img/svg/hotel_card_badges/{{ badge.icon }}.svg">
                                                </div>
                                            {% elseif badge.type == 'option' %}
                                                {{ badge.title }}
                                            {% endif %}
                                        </div>
                                    {% endfor %}
                                </div>
                            {% endif %}
                        </div>
                    </div>
                </div>`
            ),
        }
    }

    this._getPlaceBalloonLayout = function(is_current = false) {
        const obj = this;

        const placeBalloonLayout = (function() {
            const MyBalloonLayout = obj._ymaps.templateLayoutFactory.createClass(
                `<div class="Map__balloon Map__balloon--place js-mapBalloon">
                    <div class="Map__balloon-wrapper">
                        $[[options.contentLayout]]
                    </div>
                </div>`, {
                /**
                 * Строит экземпляр макета на основе шаблона и добавляет его в родительский HTML-элемент.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#build
                 * @function
                 * @name build
                 */
                build: function () {
                    this.constructor.superclass.build.call(this);

                    this.$element = this.getElement().firstElementChild;

                    // отмечаем избранное
                    checkFavoriteList();
                },

                /**
                 * Удаляет содержимое макета из DOM.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#clear
                 * @function
                 * @name clear
                 */
                clear: function () {
                    this.constructor.superclass.clear.call(this);
                },

                /**
                 * Метод будет вызван системой шаблонов АПИ при изменении размеров вложенного макета.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 * @function
                 * @name onSublayoutSizeChange
                 */
                onSublayoutSizeChange: function () {
                    MyBalloonLayout.superclass.onSublayoutSizeChange.apply(this, arguments);

                    if(!this.$element) {
                        return;
                    }

                    this.events.fire('shapechange');
                },

                /**
                 * Закрывает балун при клике на крестик, кидая событие "userclose" на макете.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 * @function
                 * @name onCloseClick
                 */
                onCloseClick: function (e) {
                    e.preventDefault();

                    this.events.fire('userclose');
                },

                /**
                 * Используется для автопозиционирования (balloonAutoPan).
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/ILayout.xml#getClientBounds
                 * @function
                 * @name getClientBounds
                 * @returns {Number[][]} Координаты левого верхнего и правого нижнего углов шаблона относительно точки привязки.
                 */
                getShape: function () {
                    const offsetMultiplierX = -0.5;
                    const offsetMultiplierY = -1.0;

                    const offsetX = 0;
                    const offsetY = is_current ? -35 : -20;

                    const x = this.$element.offsetWidth * offsetMultiplierX + offsetX;

                    const y = isMobile()
                        ? offsetY
                        : this.$element.offsetHeight * offsetMultiplierY + offsetY;

                    this.$element.style.transform = `translate(${x}px, ${y}px)`;

                    const result = new obj._ymaps.shape.Rectangle(
                        new ymaps.geometry.pixel.Rectangle([
                            [x, y],
                            [
                                x + this.$element.offsetWidth,
                                isMobile() ? y - this.$element.offsetHeight : y + this.$element.offsetHeight
                            ]
                        ])
                    );

                    return result;
                },
            });
            return MyBalloonLayout;
        })();

        const placeBalloonContentLayout = obj._ymaps.templateLayoutFactory.createClass(
            `<div class="Balloon-place">
                <div class="Balloon-place__wrapper">
                    <div class="Balloon-place__photogallery js-balloonPlacePhotogallery">
                        {% for photo in properties.photos %}
                            <div class="Balloon-place__photogallery-slide js-balloonPlacePhotogallerySlide" style="background-color: {{ photo.hex }}">
                                <img src="{{ photo.url }}" alt="{{ photo.alt }}">
                            </div>
                        {% endfor %}
                    </div>
                    <div class="Balloon-place__body">
                        <a href="{{ properties.url }}" title="{{ properties.attr_title }}" target="_blank" class="Balloon-place__link Balloon-place__link--absolute"></a>
                        <div class="Balloon-place__category">{{ properties.category_title }}</div>
                        <div class="Balloon-place__title" title="{{ properties.title }}">{{ properties.title }}</div>
                    </div>
                </div>
            </div>`
        );

        return {
            balloonLayout: placeBalloonLayout,
            balloonContentLayout: placeBalloonContentLayout,
        }
    }

    // показываем панораму
    this.loadPanorama = () => {
        if (!this._ymaps) {
            return this._loadYandexMapScript(this.loadPanorama);
        }

        this._$panoramaContainer.classList.add('active');
        this._$mapContainer.classList.remove('active');

        // Для начала проверим, поддерживает ли плеер браузер пользователя.
        if (!this._ymaps.panorama.isSupported()) {
            // Если нет, то ничего не будем делать.
            alert('Ваш браузер не поддерживает панорамы');

            this._hotelMap._activeTabID = 'nearby_housing';
            this._hotelMap.selectTab('nearby_housing');
            return;
        }

        if (!this._panoramaPlayer) {
            // Для добавления панорамы на страницу также можно воспользоваться
            // методом panorama.createPlayer. Этот метод ищет ближайщую панораму и
            // в случае успеха создает плеер с найденной панорамой.
            this._createPlayer = this._ymaps.panorama.createPlayer(
                this._$panoramaContainer,
                [this._data.latitude, this._data.longitude],
                {
                    layer: 'yandex#panorama',
                    hotkeysEnabled: true,
                    controls: ['fullscreenControl', 'zoomControl']
                }
            );

            this._createPlayer.then((player) => {
                this._panoramaPlayer = player;

                // смотрим на точку
                this._panoramaPlayer.lookAt([this._data.latitude, this._data.longitude]);
            }).catch(error => {
                if (error.message === 'No panoramas') {
                    alert('Не удалось найти панораму');

                    this._hotelMap._activeTabID = 'nearby_housing';
                    this._hotelMap.selectTab('nearby_housing');
                } else {
                    alert('Ошибка загрузки панорамы: ' + error.message);

                    this._hotelMap._activeTabID = 'nearby_housing';
                    this._hotelMap.selectTab('nearby_housing');
                }
            });
        }
    }

    this.setHotelsVisible = (state) => {
        this._map.geoObjects.each(placemark => {
            if (placemark.options.get('name') == closestHotelName) {
                placemark.options.set('visible', state);
            }
        });
    }

    // показываем карту
    this.loadNearbyHousing = () => {
        if (!this._ymaps) {
            return this._loadYandexMapScript(this.loadNearbyHousing);
        }

        this._$panoramaContainer.classList.remove('active');
        this._$mapContainer.classList.add('active');

        if (this.isLoadedNearbyHousing === true) {
            this._map.container.fitToViewport();

            return;
        }

        // рисуем геообъекты только один раз при инициализации
        this.isLoadedNearbyHousing = true;

        this._createMap();

        const events = {
            click: (e) => {
                if (this.balloonNearbyHotel) {
                    this.balloonNearbyHotel.close();
                }
            },
            mouseenter: (e) => {
                let $icon  = e.get('target').getOverlaySync().getLayoutSync().getParentElement().querySelector('.icon');

                $icon.classList.add('active');
                $icon.classList.add('privettur');
                $icon.classList.remove('home');
            },
            mouseleave: (e) => {
                let $icon  = e.get('target').getOverlaySync().getLayoutSync().getParentElement().querySelector('.icon');

                $icon.classList.add('home');
                $icon.classList.remove('privettur');
                $icon.classList.remove('active');
            },
            mapchange: (e) => {
                let target = e.get('target');
                let mapchange = () => {
                    try {
                        let $svg = target.getOverlaySync().getLayoutSync().getParentElement().children[0].children[0];

                        // добавляем свою верстку
                        if (!$svg.classList.contains('current')) {
                            $svg.classList.add('current');

                            // $svg.style.backgroundPosition = '';
                            // $svg.style.backgroundSize = '';
                            // $svg.style.backgroundImage = '';

                            $svg.innerHTML = `<div class="container"><div class="icon home"></div></div>`;
                        }
                    } catch(e) {
                        // вызываем сами себя, пока не появится карта
                        setTimeout(mapchange, 100);
                    }
                }

                mapchange();
            }
        };

        const placesEvents = {
            click: (e) => {
                if (this.balloonNearbyHotel) {
                    this.balloonNearbyHotel.close();
                }
            },
        };

        // достопримечательности
        this._data.places.forEach(place => {
            this._addMarker({
                latitude: place.latitude,
                longitude: place.longitude,
                icon: place.map_icon,
                events: !place.is_current ? placesEvents : events
            }, place);
        });

        if (!this._data.is_place) {
            // маркер объекта на котором находимся
            this._addMarker({
                latitude: this._data.latitude,
                longitude: this._data.longitude,
                icon: 'bigBlueHomeNew',
                events
            }, this._data.hotel);
        }

        // объекты рядом
        this._data.hotels.forEach(hotel => {
            this._addMarker({
                latitude: hotel.latitude,
                longitude: hotel.longitude,
                icon: closestHotelName,
            }, hotel);
        });
    }

    this.init = () => {
        this._$mapContainer.innerHTML = '';
    }
    this.init();
}

function HotelMap(options = {}) {
    // наследуемся
    EventEmitter.call(this);

    this._$container = document.getElementById('Map');
    if (!this._$container) {
        return;
    }

    this._$hotelMap     = this._$container.querySelector('.js-hotelMap');
    this._$hotelMapData = this._$container.querySelector('.js-hotelMapData');
    this._$tabs         = this._$container.querySelectorAll('.js-tabItem');
    this._activeTabID   = '';
    this._mapApi        = null;
    this._options       = Object.assign({
        useExternalBalloons: {},
    }, options);

    this._whenInitialized = new Promise((resolve) => {
        this._whenInitializedResolve = resolve;
    });

    this.whenInitialized = function(cb) {
        return this._whenInitialized.then(cb);
    }

    // загрузка активных табов
    this.selectTab = (alias) => {
        // ставим активный таб
        this._$tabs.forEach($item => {
            if ($item.dataset.id === alias) {
                $item.classList.add('active');
            } else {
                $item.classList.remove('active');
            }
        });

        this.loadActiveTab();
    }

    // загрузка активных табов
    this.loadActiveTab = () => {
        // ставим активный таб
        this._$tabs.forEach($item => {
            if ($item.classList.contains('active')) {
                $item.classList.remove('active');
            }

            if ($item.dataset.id === this._activeTabID) {
                $item.classList.add('active');
            }
        });

        if (this._activeTabID === 'nearby_housing') {
            return this._mapApi.loadNearbyHousing();
        }

        if (this._activeTabID === 'panorama') {
            return this._mapApi.loadPanorama();
        }
    }

    this.setHotelsVisible = (state) => {
        this._mapApi.setHotelsVisible(state);
    }

    this.fitToViewport = () => {
        setTimeout(() => {
            this._mapApi.fitToViewport();
        }, 100);
    }

    this.init = () => {
        this._mapData = JSON.parse(this._$hotelMapData.innerHTML);

        this._mapData.controls = [];
        this._mapData.behaviors = ['multiTouch', 'drag'];

        this._mapApi = new MapApi({
            data: this._mapData,
            hotelMap: this,
            useExternalBalloons: this._options?.useExternalBalloons,
        });

        this._mapApi.whenInitialized(() => {
            this._whenInitializedResolve();
        });

        // ставим активный таб
        this._$tabs.forEach($item => {
            if ($item.classList.contains('active')) {
                this._activeTabID = $item.dataset.id;
            }

            $item.addEventListener('click', () => {
                // повторный клик на таб
                if (this._activeTabID === $item.dataset.id) {
                    return false;
                }

                this._activeTabID = $item.dataset.id;

                this.loadActiveTab();
            });
        });

        // ставим обсервер на карту
        observeElement(this._$container, this.loadActiveTab);
    }

    this.init();
}

// цепочка прототипов
HotelMap.prototype = Object.create(EventEmitter.prototype, {
    constructor: {
        value: HotelMap,
        enumerable: false,
        writable: true,
    }
});

MapApi.prototype.useCustomControls = function() {
    // зум
    this.createZoomControl();

    // геолокация пользователя
    this.createGeoLocationControl();

    // полноэкранный режим
    this.createFullScreenControl();

    // слои
    this.createTypeSelector();

    // "Маршруты"
    this.createRouteControl();

    // "Линейка"
    this.createRulerControl();
}

MapApi.prototype.createZoomControl = function() {
    let layout = ymaps.templateLayoutFactory.createClass(`\
            <div class="Ymaps__zoom-container">\
                <div class="Ymaps__zoom-in Ymaps__zoom-button"></div>\
                <div class="Ymaps__zoom-out Ymaps__zoom-button"></div>\
            </div>`, {
        build: function () {
            // Calling the "build" parent method.
            this.constructor.superclass.build.call(this);

            this.zoomInCallback = ymaps.util.bind(this.zoomIn, this);
            this.zoomOutCallback = ymaps.util.bind(this.zoomOut, this);

            this.getElement().querySelector('.Ymaps__zoom-in').addEventListener('click', this.zoomInCallback);
            this.getElement().querySelector('.Ymaps__zoom-out').addEventListener('click', this.zoomOutCallback);
        },

        clear: () => {
            this.getElement().querySelector('.Ymaps__zoom-in').removeEventListener('click', this.zoomInCallback);
            this.getElement().querySelector('.Ymaps__zoom-out').removeEventListener('click', this.zoomOutCallback);

            this.constructor.superclass.clear.call(this);
        },

        zoomIn: () => {
            this._map.setZoom(this._map.getZoom() + 1, {checkZoomRange: true});
        },

        zoomOut: () => {
            this._map.setZoom(this._map.getZoom() - 1, {checkZoomRange: true});
        }
    });

    let zoomControl = new ymaps.control.ZoomControl({
        options: {
            size: "small",
            position: {
                right: '20px',
                bottom: '100px',
            },
            layout,
        },
    });

    this._map.controls.add(zoomControl);
}

MapApi.prototype.createGeoLocationControl = function() {
    let layout = ymaps.templateLayoutFactory.createClass(`<div class="Ymaps__geo-location-button"></div>`);

    let geolocationControl = new ymaps.control.GeolocationControl({
        options: {
            position: {
                right: '20px',
                bottom: '30px',
            },
            layout,
        },
    });

    geolocationControl.events.add('locationchange', function (event) {
        geolocationControl._layout._parentElement.querySelector('.Ymaps__geo-location-button').classList.toggle('is-active');
    });

    this._map.controls.add(geolocationControl);
}

MapApi.prototype.createTypeSelector = function() {
    let layout = ymaps.templateLayoutFactory.createClass(`<div class="Ymaps__type-selector-button"></div>`);

    let button = new ymaps.control.Button({
        options: {
            position: {
                right: '80px',
                top: '20px',
                size: 'small',
            },
            layout,
        },
    });

    let routePanel = new ymaps.control.TypeSelector({
        options: {
            position: {
                right: '80px',
                top: '68px',
                size: 'small',
            },
            visible: true,
        },
    });

    this._map.controls.add(button);
    this._map.controls.add(routePanel);

    // нажали кнопку
    button.events.add(['select'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__type-selector-button').classList.add('is-active');

        routePanel.expand();
    });

    // отжали кнопку
    button.events.add(['deselect'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__type-selector-button').classList.remove('is-active');
    });

    // открыли меню
    routePanel.events.add(['expand'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__type-selector-button').classList.add('is-active');

    });

    // закрыли меню
    routePanel.events.add(['collapse'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__type-selector-button').classList.remove('is-active');

        // нужна небольшая задержка, а то срабатывает 'select' у элемента 'button' и кнопку нельзя отжать
        setTimeout(() => {
            button.state.set({
                selected: false,
            });
        }, 500)
    });
}

MapApi.prototype.createFullScreenControl = function() {
    let layout = ymaps.templateLayoutFactory.createClass(`<div class="Ymaps__full-screen-button"></div>`);

    let geolocationControl = new ymaps.control.FullscreenControl({
        options: {
            position: {
                right: '20px',
                top: '20px',
            },
            layout,
        },
    });

    this._map.controls.add(geolocationControl);

    geolocationControl.events.add(['select', 'deselect'], function (e) {
        geolocationControl._layout._parentElement.querySelector('.Ymaps__full-screen-button').classList.toggle('is-active');
    });
}

MapApi.prototype.createRouteControl = function() {
    let layout = ymaps.templateLayoutFactory.createClass(`<div class="Ymaps__route-button"><div class="icon"></div><div class="text">Маршрут</div></div>`);

    let button = new ymaps.control.Button({
        options: {
            position: {
                left: '20px',
                top: '20px',
                size: 'small',
            },
            layout,
        },
    });

    let routePanel = new ymaps.control.RoutePanel({
        options: {
            position: {
                left: '20px',
                top: '80px',
            },
            visible: false,
        },
    });

    this._map.controls.add(button);
    this._map.controls.add(routePanel);

    button.events.add(['select'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__route-button').classList.add('is-active');
        routePanel.options.set('visible', true);
    });

    button.events.add(['deselect'], function (e) {
        button._layout._parentElement.querySelector('.Ymaps__route-button').classList.remove('is-active');
        routePanel.options.set('visible', false);
    });
}

MapApi.prototype.createRulerControl = function() {
    // скрытая кнопка
    let hiddenButton = new ymaps.control.RulerControl({
        options: {
            position: {
                right: '100px',
                top: '20px',
            },
            scaleLine: false,
            visible: false,
        },
    });
    this._map.controls.add(hiddenButton);

    let layout = ymaps.templateLayoutFactory.createClass(`<div class="Ymaps__ruler-button"></div>`, {
        build: function () {
            // Calling the "build" parent method.
            this.constructor.superclass.build.call(this);

            this.clickCallback = ymaps.util.bind(this.click, this);

            this.getElement().addEventListener('click', this.clickCallback);
        },

        clear: () => {
            this.getElement().removeEventListener('click', this.clickCallback);

            this.constructor.superclass.clear.call(this);
        },

        click: (e) => {
            let result = e.target.classList.toggle('is-active');

            if (result) {
                hiddenButton.select();
            } else {
                hiddenButton.deselect();
            }
        },
    });

    // кнопка которая отображается
    let button = new ymaps.control.RulerControl({
        options: {
            position: {
                right: '140px',
                top: '20px',
            },
            scaleLine: false,
            layout,
        },
    });

    this._map.controls.add(button);
}

export default HotelMap;
